import React, { useState, useEffect } from 'react';
import { grade, gradeColor } from '../db';
import { Badge, Card, Avatar, Stat, NoData } from '../ui.jsx';
import { useCountUp, StatCard, useReveal, Reveal } from '../components/StatCard';

// ── Teacher sub-pages ─────────────────────────────────────────────

function TeacherDash({ db, teacher, name, photo, setPage }) {
  const todayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][new Date().getDay()];
  const today = new Date().toISOString().split('T')[0];
  const myClasses = (teacher.cls || '').split(',').map(x => x.trim()).filter(Boolean);
  const myCls = myClasses.join(', ');
  const myStudents = db.students.filter(s => myClasses.length === 0 || myClasses.includes(s.cls));
  const todayPers = db.tt.filter(p => p.day === todayName && (p.tea === name || (teacher.ln && p.tea?.includes(teacher.ln)))).length;
  const todayAttCount = Object.keys(db.att[today] || {}).length;
  const now = new Date();
  const effectiveStatusT = e => {
    if (e.dt && e.startTime && e.endTime) {
      const start = new Date(e.dt + 'T' + e.startTime);
      const end = new Date(e.dt + 'T' + e.endTime);
      if (!isNaN(start) && !isNaN(end)) {
        if (now >= start && now <= end) return 'Ongoing';
        if (now > end) return 'Completed';
        return 'Upcoming';
      }
    } else if (e.dt) {
      const day = new Date(e.dt); day.setHours(23, 59, 59);
      return now > day ? 'Completed' : 'Upcoming';
    }
    return e.st || 'Upcoming';
  };
  const upcomingExams = (db.exams || []).filter(e => {
    if (!e.dt) return false;
    const st = effectiveStatusT(e);
    if (st === 'Completed') return false;
    const diff = (new Date(e.dt) - now) / 86400000;
    return diff >= -1 && diff <= 14; // include today/ongoing
  }).sort((a, b) => (a.dt || '').localeCompare(b.dt || ''));

  const statStyle = (accent) => ({
    background: '#fff', borderRadius: 16, padding: '20px 24px',
    border: '1px solid #e8edf5', boxShadow: '0 2px 12px rgba(0,32,69,0.06)',
    display: 'flex', alignItems: 'center', gap: 16,
  });

  return (
    <div className="teacher-dashboard-page">
      {/* Welcome */}
      <div style={{ background: 'linear-gradient(135deg,#1960a3 0%,#6366f1 100%)', borderRadius: 20, padding: '28px 32px', marginBottom: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 8px 32px rgba(25,96,163,0.2)' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>Teacher Portal</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#fff', fontFamily: 'Manrope,sans-serif', marginBottom: 4 }}>Welcome back, {teacher.fn || 'Teacher'}</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
          {myCls && <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '4px 14px', fontSize: 12, color: '#fff', fontWeight: 600 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>class</span> Class Teacher — {myCls}
          </div>}
        </div>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 800, color: '#fff', flexShrink: 0, overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
          {photo
            ? <img src={photo} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : name.trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'T'
          }
        </div>
      </div>

      {/* Stats */}
      <div className="teacher-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 28 }}>
        {[
          { icon: 'group', value: myStudents.length, label: 'My Students', sub: myCls || 'No class', accent: '#1960a3', bg: '#dbeafe' },
          { icon: 'schedule', value: todayPers, label: 'Periods Today', sub: todayName, accent: '#f59e0b', bg: '#fef3c7' },
          { icon: 'how_to_reg', value: todayAttCount, label: 'Marked Today', sub: 'attendance', accent: '#059669', bg: '#d1fae5' },
          { icon: 'quiz', value: upcomingExams.length, label: 'Upcoming Exams', sub: 'next 14 days', accent: '#8b5cf6', bg: '#ede9fe' },
        ].map(s => (
          <div key={s.label} style={statStyle(s.accent)}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 22, color: s.accent }}>{s.icon}</span>
            </div>
            <div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#1e293b', fontFamily: 'Manrope,sans-serif', lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginTop: 3 }}>{s.label}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{s.sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Today's timetable + upcoming exams */}
      <div className="teacher-dashboard-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf5', boxShadow: '0 2px 12px rgba(0,32,69,0.06)', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#1960a3', fontVariationSettings: "'FILL' 1" }}>schedule</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: '#1e293b', fontFamily: 'Manrope,sans-serif' }}>Today's Schedule</span>
            <span style={{ marginLeft: 'auto', fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>{todayName}</span>
          </div>
          <div style={{ padding: '8px 0' }}>
            {db.tt.filter(p => p.day === todayName && (p.tea === name || (teacher.ln && p.tea?.includes(teacher.ln)))).length === 0
              ? <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>No periods today</div>
              : db.tt.filter(p => p.day === todayName && (p.tea === name || (teacher.ln && p.tea?.includes(teacher.ln)))).map((p, i) => {
                const sl = db.slots.find(s => s.id === p.sid);
                return <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: '1px solid #f8fafc' }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#1960a3', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{p.su || '—'}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{p.cls} {sl ? `· ${sl.s}–${sl.e}` : ''}</div>
                  </div>
                  {p.rm && <div style={{ fontSize: 10, color: '#64748b', background: '#f1f5f9', padding: '2px 8px', borderRadius: 6 }}>Rm {p.rm}</div>}
                </div>;
              })
            }
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf5', boxShadow: '0 2px 12px rgba(0,32,69,0.06)', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#8b5cf6', fontVariationSettings: "'FILL' 1" }}>quiz</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: '#1e293b', fontFamily: 'Manrope,sans-serif' }}>Upcoming Exams</span>
          </div>
          <div style={{ padding: '8px 0' }}>
            {upcomingExams.length === 0
              ? <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>No upcoming exams</div>
              : upcomingExams.slice(0, 5).map(e => (
                <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: '1px solid #f8fafc' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#8b5cf6' }}>quiz</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{e.name}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{e.cls || 'All'} · {e.dt || 'TBD'}</div>
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#8b5cf6', background: '#ede9fe', padding: '2px 8px', borderRadius: 6 }}>{e.su || '—'}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                    background: effectiveStatusT(e) === 'Ongoing' ? '#fef3c7' : '#ede9fe',
                    color: effectiveStatusT(e) === 'Ongoing' ? '#d97706' : '#8b5cf6' }}>
                    {effectiveStatusT(e) === 'Ongoing' ? '🔴 Now' : 'Soon'}
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </div>

      {/* Class attendance summary */}
      {myClasses.length > 0 && (() => {
        const stu = db.students.filter(s => myClasses.includes(s.cls));
        const rows = stu.map(s => {
          let sp = 0, sa = 0; Object.values(db.att).forEach(d => { const v = d[s.id]; if (v === 'P') sp++; else if (v === 'A') sa++; });
          const tot = sp + sa; const pct = tot > 0 ? Math.round(sp / tot * 100) : null;
          return { s, sp, sa, tot, pct };
        }).filter(r => r.tot > 0).sort((a, b) => (a.pct || 0) - (b.pct || 0)).slice(0, 5);
        if (!rows.length) return null;
        return (
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf5', boxShadow: '0 2px 12px rgba(0,32,69,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#dc2626', fontVariationSettings: "'FILL' 1" }}>warning</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#1e293b', fontFamily: 'Manrope,sans-serif' }}>Low Attendance — Class {myCls}</span>
              </div>
              <button onClick={() => setPage('tatt')} style={{ fontSize: 11, color: '#1960a3', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer' }}>View all →</button>
            </div>
            <div style={{ padding: '8px 0' }}>
              {rows.map(({ s, sp, sa, tot, pct }) => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: '1px solid #f8fafc' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#1960a3,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                    {(s.fn || '?')[0]}{(s.ln || '')[0]}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{s.fn} {s.ln}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>P:{sp} A:{sa}</div>
                  </div>
                  <span style={{
                    fontSize: 12, fontWeight: 800, padding: '2px 10px', borderRadius: 20,
                    background: pct >= 75 ? '#dcfce7' : '#fee2e2', color: pct >= 75 ? '#059669' : '#dc2626'
                  }}>{pct}%</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}


export default TeacherDash;
